import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type Key, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { CardSkeleton } from "@/components/common/CardSkeleton";
import { usePrintMode } from "@/hooks/usePrintMode";

interface RecordListProps<T> {
  items: readonly T[];
  getItemId: (item: T) => Key;
  renderTitle: (item: T) => ReactNode;
  renderSummary: (item: T) => ReactNode;
  renderDetails?: (item: T) => ReactNode;
  renderActions?: (item: T) => ReactNode;
  renderThumbnail?: (item: T) => ReactNode;
  emptyState: ReactNode;
  loading?: boolean;
  loadingState?: ReactNode;
  errorState?: ReactNode;
  /** Reset expansion when a page, filter, or sort changes, without resetting data state. */
  resetKey?: Key;
}

export function RecordItem({
  title,
  summary,
  details,
  actions,
  thumbnail,
}: {
  title: ReactNode;
  summary: ReactNode;
  details?: ReactNode;
  actions?: ReactNode;
  thumbnail?: ReactNode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const printing = usePrintMode();
  const detailsId = useId();
  return (
    <Box
      component="li"
      sx={{
        py: 2,
        minWidth: 0,
        "& + li": { borderTop: 1, borderColor: "divider" },
        "@media print": { breakInside: "avoid" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {thumbnail && (
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{thumbnail}</Box>
        )}
        <Box
          role="heading"
          aria-level={3}
          sx={{
            typography: "subtitle1",
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Box>
      </Box>
      <Box sx={{ mt: 2, minWidth: 0 }}>{summary}</Box>
      {details && (
        <Box id={detailsId}>
          <Collapse in={expanded || printing} mountOnEnter unmountOnExit timeout={0}>
            <Box sx={{ mt: 2, minWidth: 0 }}>{details}</Box>
          </Collapse>
        </Box>
      )}
      {(details || actions) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            "@media print": { display: "none" },
          }}
        >
          {actions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                minWidth: 0,
                flex: 1,
                "& > .MuiStack-root": { justifyContent: "flex-start" },
              }}
            >
              {actions}
            </Box>
          )}
          {actions && details && <Divider orientation="vertical" flexItem sx={{ my: 1 }} />}
          {details && (
            <Tooltip
              title={t(
                expanded ? "common.records.collapseDetails" : "common.records.expandDetails",
              )}
            >
              <IconButton
                type="button"
                aria-label={t(
                  expanded ? "common.records.collapseDetails" : "common.records.expandDetails",
                )}
                aria-expanded={expanded}
                aria-controls={detailsId}
                onClick={() => setExpanded((previous) => !previous)}
                sx={{
                  minHeight: 44,
                  minWidth: 44,
                  marginInlineStart: "auto",
                  flexShrink: 0,
                  "@media print": { display: "none" },
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Compact records within the owning section Paper; data and paging stay with the page. */
export function RecordList<T>({
  items,
  getItemId,
  renderTitle,
  renderSummary,
  renderDetails,
  renderActions,
  renderThumbnail,
  emptyState,
  loading = false,
  loadingState,
  errorState,
  resetKey,
}: RecordListProps<T>) {
  const { t } = useTranslation();
  if (errorState) return <Box sx={{ py: 2 }}>{errorState}</Box>;
  if (loading)
    return (
      loadingState ?? (
        <Box role="status" aria-label={t("common.loading")} sx={{ py: 2 }}>
          {[0, 1, 2].map((id) => (
            <Box key={id} sx={{ mb: 2 }} aria-hidden="true">
              <CardSkeleton height={24} width="65%" />
              <CardSkeleton height={18} sx={{ mt: 1 }} />
            </Box>
          ))}
        </Box>
      )
    );
  if (!items.length)
    return <Box sx={{ py: 3, minWidth: 0, overflowWrap: "anywhere" }}>{emptyState}</Box>;
  return (
    <Box component="ul" key={resetKey} sx={{ listStyle: "none", p: 0, m: 0, minWidth: 0 }}>
      {items.map((item) => (
        <RecordItem
          key={getItemId(item)}
          title={renderTitle(item)}
          summary={renderSummary(item)}
          details={renderDetails?.(item)}
          actions={renderActions?.(item)}
          thumbnail={renderThumbnail?.(item)}
        />
      ))}
    </Box>
  );
}

export interface RecordField {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
}

/** Label/value presentation only; the owning page chooses and formats its fields. */
export function RecordFields({ fields }: { fields: readonly RecordField[] }) {
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1.5,
        m: 0,
      }}
    >
      {fields.map(({ label, value, hint }, index) => (
        <Box key={index} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Typography
            component="dt"
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500, lineHeight: 1.4 }}
          >
            {label}
          </Typography>
          <Box
            component="dd"
            sx={{ m: 0, mt: 0.5, typography: "body1", color: "text.primary", fontWeight: 600 }}
          >
            {value}
            {hint !== undefined && hint !== null && hint !== "" && (
              <Typography
                component="div"
                variant="caption"
                sx={{
                  mt: 0,
                  fontSize: "0.625rem",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[400]
                      : theme.palette.grey[600],
                }}
              >
                {hint}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Selects a page-owned list or table without mounting both sets of record controls. */
export function ResponsiveRecordContent({
  mobile,
  children,
}: {
  mobile: ReactNode;
  children: ReactNode;
}) {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("md")) ? mobile : children;
}
